import { Controller } from '@hotwired/stimulus';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const PROMOTER_COLOR = '#198754';
const PASSIVE_COLOR = '#ffc107';
const DETRACTOR_COLOR = '#dc3545';
const NPS_LINE_COLOR = '#0d6efd';

export default class extends Controller {
  static values = {
    monthlyTrend: Array,
    quarterlyTrend: Array,
  };

  static targets = ['monthlyNps', 'quarterlyNps', 'monthlyDistribution'];

  connect() {
    this.charts = [];
    this.renderMonthlyNps();
    this.renderQuarterlyNps();
    this.renderMonthlyDistribution();
  }

  disconnect() {
    this.charts.forEach((chart) => chart.destroy());
    this.charts = [];
  }

  renderMonthlyNps() {
    if (!this.hasMonthlyNpsTarget) return;
    const points = this.monthlyTrendValue;
    const chart = new Chart(this.monthlyNpsTarget, {
      type: 'line',
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: 'NPS',
            data: points.map((p) => p.score),
            borderColor: NPS_LINE_COLOR,
            backgroundColor: `${NPS_LINE_COLOR}20`,
            borderWidth: 2,
            tension: 0.3,
            spanGaps: false,
            pointRadius: 4,
            pointBackgroundColor: NPS_LINE_COLOR,
          },
        ],
      },
      options: this.scoreChartOptions(points),
    });
    this.charts.push(chart);
  }

  renderQuarterlyNps() {
    if (!this.hasQuarterlyNpsTarget) return;
    const points = this.quarterlyTrendValue;
    const chart = new Chart(this.quarterlyNpsTarget, {
      type: 'line',
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: 'NPS',
            data: points.map((p) => p.score),
            borderColor: NPS_LINE_COLOR,
            backgroundColor: `${NPS_LINE_COLOR}20`,
            borderWidth: 2,
            tension: 0.3,
            spanGaps: false,
            pointRadius: 5,
            pointBackgroundColor: NPS_LINE_COLOR,
          },
        ],
      },
      options: this.scoreChartOptions(points),
    });
    this.charts.push(chart);
  }

  renderMonthlyDistribution() {
    if (!this.hasMonthlyDistributionTarget) return;
    const points = this.monthlyTrendValue;
    const chart = new Chart(this.monthlyDistributionTarget, {
      type: 'bar',
      data: {
        labels: points.map((p) => p.label),
        datasets: [
          {
            label: 'Promoters (9–10)',
            data: points.map((p) => p.promoters),
            backgroundColor: PROMOTER_COLOR,
          },
          {
            label: 'Passives (7–8)',
            data: points.map((p) => p.passives),
            backgroundColor: PASSIVE_COLOR,
          },
          {
            label: 'Detractors (0–6)',
            data: points.map((p) => p.detractors),
            backgroundColor: DETRACTOR_COLOR,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              footer: (items) => {
                const total = items.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
                return `Total responses: ${total}`;
              },
            },
          },
        },
        scales: {
          x: { stacked: true },
          y: {
            stacked: true,
            beginAtZero: true,
            title: { display: true, text: 'Responses' },
            ticks: { precision: 0 },
          },
        },
      },
    });
    this.charts.push(chart);
  }

  scoreChartOptions(points) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => {
              const point = points[item.dataIndex];
              if (point.score === null || point.score === undefined) {
                return `Not enough data (N=${point.n})`;
              }
              return `NPS ${point.score} (N=${point.n})`;
            },
          },
        },
      },
      scales: {
        y: {
          min: -100,
          max: 100,
          title: { display: true, text: 'NPS score' },
          ticks: { stepSize: 25 },
          grid: { color: (ctx) => (ctx.tick.value === 0 ? '#999' : 'rgba(0,0,0,0.1)') },
        },
      },
    };
  }
}
